import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";

const TIMEZONES = ["Europe/Paris", "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Rome", "Europe/Lisbon", "Europe/Brussels", "Europe/Amsterdam",
  "Europe/Zurich", "Europe/Vienna", "Europe/Prague", "Europe/Warsaw", "Europe/Athens", "Europe/Istanbul", "Europe/Kyiv", "Europe/Moscow", "Asia/Dubai",
  "Asia/Tbilisi", "Asia/Yerevan", "Asia/Almaty", "Asia/Tashkent", "Asia/Bangkok", "Asia/Bali", "America/New_York", "America/Los_Angeles", "America/Sao_Paulo"];

const DEFAULT_DAY = { on: true, from: "09:00", to: "21:00" };

export const CHANNELS = ["widget", "whatsapp", "instagram", "phone"];
const CHANNEL_ICON = { widget: "💬", whatsapp: "🟢", instagram: "📸", phone: "📞" };

export const defaultAiSchedule = () => ({
  enabled: false,
  days: Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((d) => [String(d), { ...DEFAULT_DAY }])),
  off_message: "",
  scope: "all",
  channels: { widget: true, whatsapp: true, instagram: true, phone: true },
  per_channel: {},
});

function DayGrid({ days, onChange, dayNames, testPrefix }) {
  const setDay = (d, patch) => onChange({ ...days, [d]: { ...days[d], ...patch } });
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4, 5, 6].map((d) => {
        const day = days[String(d)] || DEFAULT_DAY;
        return (
          <div key={d} className="flex items-center gap-3 text-sm" data-testid={`${testPrefix}-day-${d}`}>
            <label className="flex items-center gap-2 w-24 cursor-pointer">
              <input type="checkbox" checked={!!day.on} data-testid={`${testPrefix}-day-${d}-on`}
                onChange={(e) => setDay(String(d), { on: e.target.checked })} className="accent-black w-4 h-4" />
              <span className={day.on ? "font-medium" : "text-gray-400"}>{dayNames[d]}</span>
            </label>
            <input type="time" value={day.from} disabled={!day.on} data-testid={`${testPrefix}-day-${d}-from`}
              onChange={(e) => setDay(String(d), { from: e.target.value })}
              className="border-2 border-gray-200 rounded-lg px-2 py-1 text-sm bg-white disabled:opacity-40 focus:border-black outline-none" />
            <span className="text-gray-400">—</span>
            <input type="time" value={day.to} disabled={!day.on} data-testid={`${testPrefix}-day-${d}-to`}
              onChange={(e) => setDay(String(d), { to: e.target.value })}
              className="border-2 border-gray-200 rounded-lg px-2 py-1 text-sm bg-white disabled:opacity-40 focus:border-black outline-none" />
          </div>
        );
      })}
    </div>
  );
}

export default function AiScheduleEditor({ value, onChange, timezone, onTimezoneChange }) {
  const { t } = useTranslation();
  const [tz, setTz] = useState(timezone || "");
  const [tab, setTab] = useState("widget");
  const saveTz = async (v) => {
    setTz(v);
    try {
      await axios.put(`${API}/team/timezone`, { timezone: v });
      onTimezoneChange?.(v);
      toast.success(`${t('settings.timezoneSaved')}: ${v}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const def = defaultAiSchedule();
  const sch = { ...def, ...(value || {}), days: { ...def.days, ...((value || {}).days || {}) },
    channels: { ...def.channels, ...((value || {}).channels || {}) }, per_channel: { ...((value || {}).per_channel || {}) } };
  const dayNames = t('settings.aiScheduleDays').split(',');
  const chLabel = (c) => t(`settings.aiScheduleCh.${c}`);
  const chCfg = sch.per_channel[tab] || { enabled: false, days: sch.days };
  const setChCfg = (patch) => onChange({ ...sch, per_channel: { ...sch.per_channel, [tab]: { ...chCfg, days: chCfg.days || sch.days, ...patch } } });

  return (
    <div className="mt-4 pt-3 border-t border-gray-200" data-testid="ai-schedule-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-bold text-black mb-1 text-sm">🕒 {t('settings.aiScheduleTitle')}</h4>
          <p className="text-xs text-gray-600">{t('settings.aiScheduleHint')}</p>
          <label className="mt-1.5 inline-flex items-center gap-2 text-xs text-gray-700">
            <span>{t('settings.timezone')}:</span>
            <select value={tz} onChange={(e) => saveTz(e.target.value)} data-testid="salon-timezone-select"
              className={`border-2 rounded-md px-2 py-1 text-xs bg-white ${tz ? "border-gray-300" : "border-amber-400"}`}>
              {!tz && <option value="">{t('settings.timezoneMissing')}</option>}
              {TIMEZONES.concat(tz && !TIMEZONES.includes(tz) ? [tz] : []).map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </label>
          {!tz && <p className="text-[11px] text-amber-700 mt-0.5">{t('settings.timezoneWarning')}</p>}
        </div>
        <button type="button" data-testid="ai-schedule-toggle"
          onClick={() => onChange({ ...sch, enabled: !sch.enabled })}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 shrink-0 transition-colors ${
            sch.enabled ? "border-black bg-black text-white" : "border-gray-300 bg-white text-gray-700"}`}>
          {sch.enabled ? t('settings.aiScheduleOn') : t('settings.aiScheduleAlways')}
        </button>
      </div>
      {sch.enabled && (
        <div className="mt-3 space-y-3">
          {/* Scope: one schedule for all channels vs. a schedule per channel */}
          <div className="inline-flex rounded-lg border-2 border-gray-300 overflow-hidden text-xs font-semibold" data-testid="ai-schedule-scope">
            {["all", "per_channel"].map((sc) => (
              <button key={sc} type="button" onClick={() => onChange({ ...sch, scope: sc })} data-testid={`ai-schedule-scope-${sc}`}
                className={`px-3 py-1.5 ${sch.scope === sc ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                {sc === "all" ? t('settings.aiScheduleScopeAll') : t('settings.aiScheduleScopePerChannel')}
              </button>
            ))}
          </div>

          {sch.scope !== "per_channel" ? (
            <>
              <div className="flex flex-wrap gap-2" data-testid="ai-schedule-channels">
                <span className="text-xs text-gray-600 self-center">{t('settings.aiScheduleAppliesTo')}:</span>
                {CHANNELS.map((c) => (
                  <label key={c} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border-2 cursor-pointer ${sch.channels[c] !== false ? "border-black bg-gray-50" : "border-gray-200 text-gray-400"}`}>
                    <input type="checkbox" checked={sch.channels[c] !== false} data-testid={`ai-schedule-channel-${c}`}
                      onChange={(e) => onChange({ ...sch, channels: { ...sch.channels, [c]: e.target.checked } })} className="accent-black w-3.5 h-3.5" />
                    {CHANNEL_ICON[c]} {chLabel(c)}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">{t('settings.aiScheduleChannelsHint')}</p>
              <DayGrid days={sch.days} dayNames={dayNames} testPrefix="ai-schedule" onChange={(days) => onChange({ ...sch, days })} />
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-schedule-channel-tabs">
                {CHANNELS.map((c) => {
                  const on = !!sch.per_channel[c]?.enabled;
                  return (
                    <button key={c} type="button" onClick={() => setTab(c)} data-testid={`ai-schedule-tab-${c}`}
                      className={`text-xs px-3 py-1.5 rounded-full border-2 ${tab === c ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-700"}`}>
                      {CHANNEL_ICON[c]} {chLabel(c)} <span className={`ml-1 ${tab === c ? "opacity-80" : on ? "text-green-700" : "text-gray-400"}`}>{on ? t('settings.aiScheduleOn') : t('settings.aiScheduleAlways')}</span>
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ai-schedule-ch-enabled-${tab}`}>
                <input type="checkbox" checked={!!chCfg.enabled} onChange={(e) => setChCfg({ enabled: e.target.checked })} className="accent-black w-4 h-4"
                  data-testid={`ai-schedule-ch-enabled-input-${tab}`} />
                <span>{t('settings.aiScheduleChEnabled').replace('{channel}', chLabel(tab))}</span>
              </label>
              {chCfg.enabled && (
                <DayGrid days={chCfg.days || sch.days} dayNames={dayNames} testPrefix={`ai-schedule-${tab}`} onChange={(days) => setChCfg({ days })} />
              )}
            </>
          )}

          <div className="pt-2">
            <label className="text-xs font-semibold text-gray-700">{t('settings.aiScheduleOffMsg')}</label>
            <textarea rows={2} value={sch.off_message || ""} data-testid="ai-schedule-off-message"
              onChange={(e) => onChange({ ...sch, off_message: e.target.value })}
              placeholder={t('settings.aiScheduleOffMsgPh')}
              className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none" />
            <p className="text-[11px] text-gray-500 mt-1">{t('settings.aiScheduleOffMsgHint')} {t('settings.aiSchedulePhoneHint')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
